<template>
  <div class="tw-flex tw-flex-col tw-h-full">
    <FancyServerTable
      v-if="isStateLoaded"
      ref="fancyTableRef"
      :data="orderTableData"
      :columns="orderTableHeaders"
      :enable-filters="true"
      :enable-pagination="true"
      :enable-column-visibility="true"
      :enable-column-pinning="true"
      :initial-column-pinning="
        currentSavedState?.columnPinning || {
          left: ['order_number'],
          right: ['actions'],
        }
      "
      :initial-column-visibility="currentSavedState?.columnVisibility || {}"
      :initial-column-order="currentSavedState?.columnOrder || []"
      :initial-column-sizing="currentSavedState?.columnSizing || {}"
      :initial-column-filters="currentSavedState?.columnFilters || []"
      :initial-global-filter="currentSavedState?.globalFilter || ''"
      :initial-sorting="currentSavedState?.sorting ?? []"
      :initial-page-size="currentSavedState?.pageSize || 10"
      :auto-filter="true"
      enable-row-selection
      :get-row-id="row => String(row.id)"
      @update:row-selection="handleRowSelectionChange"
      :loading="isLoading"
      @update:state="debouncedHandleTableStateChange"
      :total-rows="totalRows"
      :page-index="pageIndex"
      :page-size="pageSize"
      @fetch-data="fetchRecurringOrders"
    >
      <template #actions>
        <RecurringOrdersTableBulkActions
          :selectedOrders="selectedOrders"
          v-if="selectedOrders.length >= 1"
          @reloadTable="fetchRecurringOrders"
          @clearSelection="handleClearSelection"
        />
      </template>
      <template #config-manager>
        <FancyTableConfigManager
          :saved-configs="savedTableConfigs"
          :current-config="currentSavedState"
          :is-loading="false"
          :can-save-state="canSaveState"
          @save-config="handleSaveConfig"
          @update-config="handleUpdateConfig"
          @apply-config="handleApplyConfig"
          @delete-config="handleDeleteConfig"
          @mark-as-default="handleMarkAsDefault"
        />
      </template>
    </FancyServerTable>
  </div>
</template>

<script setup lang="ts">
import { ref, h } from 'vue';
import { RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
  FancyServerTable,
  type FancyTableColumnDef,
  type ServerTableOptions,
  useTableRenderers,
  FieldType,
  FilterType,
  FancyTableConfigManager,
} from '@/components/shared/fancyTables';

import RecurringOrdersTableRowActions from './RecurringOrdersTableRowActions.vue';
import RecurringOrdersTableBulkActions from './RecurringOrdersTableBulkActions.vue';
import { useRecurringInstructionStore } from '@/stores/recurring-instructions.store';
import { GetStatusRenderer } from '@/views/RecurringOrders/Table/Renderers/StatusRenderer';
import { GetOrderNumberRenderer } from '@/views/RecurringOrders/Table/Renderers/OrderNumberRenderer';
import { useFancyTableConfig } from '@/_helpers/composables/fancy-table-config.composable';

const fancyTableRef = ref<any>(null);
const selectedOrders = ref<any[]>([]);
// Cache to store all selected row data across pages
const selectedRowsCache = ref<Map<string, any>>(new Map());
const { GetFieldRenderer } = useTableRenderers();

const { getRecurringInstructions } = useRecurringInstructionStore();
const { t } = useI18n();

const props = defineProps<{
  customerId: number;
}>();

const isLoading = ref(false);
const orderTableData = ref<any[]>([]);
const totalRows = ref(0);
const pageIndex = ref(0);
const pageSize = ref(10);

// Map filters from TanStack Table format to API params
const mapFiltersToApiParams = (filters: any[]) => {
  const params: any = {};

  filters.forEach(filter => {
    const { id, value } = filter;

    switch (id) {
      case 'recurring_number':
        if (value) params.recurring_number = value;
        break;
      case 'supplier_name':
        if (value) params.supplier_name = value;
        break;
      case 'document_no_internal':
        if (value) params.document_no_internal = value;
        break;
      case 'name':
        if (value) params.name = value;
        break;
      case 'frequency':
        if (value && value.length > 0) params.frequency = value.join(',');
        break;
      case 'start_date':
        if (value?.from) params.start_date_after = value.from;
        if (value?.to) params.start_date_before = value.to;
        break;
      case 'end_after':
        if (value?.start !== undefined) params.end_after_min = value.start;
        if (value?.end !== undefined) params.end_after_max = value.end;
        break;
      case 'project_no':
        if (value) params.project_no = value;
        break;
      case 'balance':
        if (value?.start !== undefined) params.current_balance_min = value.start;
        if (value?.end !== undefined) params.current_balance_max = value.end;
        break;
      case 'amount':
        if (value?.start !== undefined) params.amount_min = value.start;
        if (value?.end !== undefined) params.amount_max = value.end;
        break;
      case 'created_on':
        if (value?.from) params.created_on_after = value.from;
        if (value?.to) params.created_on_before = value.to;
        break;
      case 'approved_by_name':
        if (value) params.approved_by_name = value;
        break;
      case 'rejected_by_name':
        if (value) params.rejected_by_name = value;
        break;
      case 'cleared_by_names':
        if (value) params.cleared_by_name = value;
        break;
      case 'status_display':
        if (value && value.length > 0) params.status = value.join(',');
        break;
      case 'is_clarification_requested':
        if (value !== undefined) {
          params.clarification_requested_only = value ? 'true' : 'false';
        }
        break;
      case 'range_in_months':
        if (value?.start !== undefined) params.range_in_months_min = value.start;
        if (value?.end !== undefined) params.range_in_months_max = value.end;
        break;
      case 'expected_date_processing':
        if (value?.from) params.expected_date_processing_after = value.from;
        if (value?.to) params.expected_date_processing_before = value.to;
        break;
      case 'order_triggered':
        if (value !== undefined) {
          params.order_triggered = value ? 'true' : 'false';
        }
        break;
      case 'order_triggered_on':
        if (value?.from) params.order_triggered_on_after = value.from;
        if (value?.to) params.order_triggered_on_before = value.to;
        break;
      case 'preliminary_insolvency_proceedings':
        if (value !== undefined) {
          params.preliminary_insolvency_proceedings = value ? 'true' : 'false';
        }
        break;
      case 'order_no_supplier':
        if (value) params.order_no_supplier = value;
        break;
      default:
        break;
    }
  });

  return params;
};

// Map sorting from TanStack Table format to API param
const mapSortingToApiParam = (sorting: any[]): string => {
  if (sorting.length === 0) return '';

  const sort = sorting[0];
  const { id, desc } = sort;
  let backendField = id;

  switch (id) {
    case 'recurring_number':
      backendField = 'recurring_number_without_prefix';
      break;
    case 'status_display':
      backendField = 'status';
      break;
    case 'supplier_name':
      backendField = 'supplier__name';
      break;
    case 'balance':
      backendField = 'balance_sort';
      break;
    case 'amount':
      backendField = 'amount_sort';
      break;
    case 'approved_by_name':
    case 'rejected_by_name':
      backendField = 'released_by__first_name';
      break;
    case 'project_no':
      backendField = 'project_no_sort';
      break;
    case 'frequency':
      backendField = 'frequency_sort';
      break;
    default:
      backendField = id;
  }

  return desc ? `-${backendField}` : backendField;
};

// Fetch recurring orders with server-side filtering and sorting
const fetchRecurringOrders = async (options?: ServerTableOptions) => {
  isLoading.value = true;

  try {
    const currentPageIndex = options?.pageIndex ?? 0;
    const currentPageSize = options?.pageSize ?? 10;
    const filters = options?.filters ?? [];
    const sorting = options?.sorting ?? [];

    // Update pagination state
    pageIndex.value = currentPageIndex;
    pageSize.value = currentPageSize;

    // Map filters to API params
    const filterParams = mapFiltersToApiParams(filters);

    // Map sorting to API param
    const orderingParam = mapSortingToApiParam(sorting);

    const response = await getRecurringInstructions({
      page: currentPageIndex + 1,
      page_size: currentPageSize,
      customer: props.customerId,
      ...filterParams,
      ...(orderingParam && { ordering: orderingParam }),
    });

    orderTableData.value = response.results;
    totalRows.value = response.count;
  } catch (error) {
    console.error('Error fetching recurring orders:', error);
    orderTableData.value = [];
    totalRows.value = 0;
  } finally {
    isLoading.value = false;
  }
};

const handleClearSelection = () => {
  fancyTableRef.value.resetRowSelection();
  selectedRowsCache.value.clear();
  selectedOrders.value = [];
};

// Initialize the table config composable
const {
  savedTableConfigs,
  currentSavedState,
  canSaveState,
  debouncedHandleTableStateChange,
  handleSaveConfig,
  handleApplyConfig,
  handleUpdateConfig,
  handleDeleteConfig,
  handleMarkAsDefault,
  isStateLoaded,
} = useFancyTableConfig({
  tableName: 'recurring_orders',
  fancyTableRef,
  tableLoader: async () => {
    await fetchRecurringOrders();
  },
});

const orderStatusOptions = [
  {
    label: t('orders.titles.clearing'),
    value: 'CLEARING',
    class:
      'tw-bg-purple-100 tw-border-purple-100 tw-text-purple-500 tw-px-2 tw-py-1 tw-text-sm tw-font-thin',
  },
  {
    label: t('orders.titles.rejected'),
    value: 'REJECTED',
    class:
      'tw-bg-red-100 tw-border-red-100 tw-text-red-500 tw-px-2 tw-py-1 tw-text-sm tw-font-thin',
  },
  {
    label: t('orders.titles.approved'),
    value: 'APPROVED',
    class:
      'tw-bg-green-100 tw-border-green-100 tw-text-green-500 tw-px-2 tw-py-1 tw-text-sm tw-font-thin',
  },
  {
    label: t('orders.titles.pending'),
    value: 'PENDING_APPROVAL',
    class:
      'tw-bg-amber-100 tw-border-amber-100 tw-text-amber-500 tw-px-2 tw-py-1 tw-text-sm tw-font-thin',
  },
  {
    label: t('orders.titles.draft'),
    value: 'DRAFT',
    class:
      'tw-bg-gray-100 tw-border-gray-100 tw-text-gray-500 tw-px-2 tw-py-1 tw-text-sm tw-font-thin',
  },
];

const frequencyOptions = [
  {
    label: t('orders.recurring.frequency_daily'),
    value: 'DAILY',
  },
  {
    label: t('orders.recurring.frequency_weekly'),
    value: 'WEEKLY',
  },
  {
    label: t('orders.recurring.frequency_monthly'),
    value: 'MONTHLY',
  },
  {
    label: t('orders.recurring.frequency_quarterly'),
    value: 'QUARTERLY',
  },
  {
    label: t('orders.recurring.frequency_yearly'),
    value: 'YEARLY',
  },
];

const orderTableHeaders: FancyTableColumnDef<any>[] = [
  {
    header: t('orders.titles.order_no'),
    accessorKey: 'recurring_number',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: false,
    fieldType: FieldType.TEXT,
    cell: GetOrderNumberRenderer(),
    size: 100,
  },
  {
    header: t('orders.titles.supplier'),
    accessorKey: 'supplier_name',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    cell: info => {
      const name = info.getValue();
      if (!name) return '-';
      const isDeleted = info.row.original.supplier_is_deleted;
      if (isDeleted) {
        return h(
          'span',
          { class: 'tw-text-secondary-300 tw-cursor-default' },
          `${name} (${t('suppliers.labels.deleted')})`
        );
      }
      const supplierId = info.row.original.supplier;
      if (!supplierId) return name;
      return h(
        RouterLink,
        {
          class: 'tw-underline tw-text-secondary-500 hover:tw-text-primary-600 tw-cursor-pointer',
          to: { name: 'edit-supplier', params: { supplierId } },
        },
        () => name
      );
    },
    size: 150,
  },
  {
    header: t('orders.titles.doc_no_internal'),
    enableSorting: true,
    accessorKey: 'document_no_internal',
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 100,
  },
  {
    header: t('orders.titles.order_name'),
    enableSorting: true,
    accessorKey: 'name',
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 100,
  },
  {
    header: t('orders.recurring.frequency'),
    accessorKey: 'frequency',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    filterType: FilterType.MULTI_SELECT,
    filterFn: 'enumFilter',
    cell: GetFieldRenderer(FieldType.ENUM, {
      enumOptions: frequencyOptions,
    }),
    filterOptions: frequencyOptions,
    size: 150,
  },
  {
    header: t('orders.titles.start_date'),
    accessorKey: 'start_date',
    enableSorting: true,
    enableColumnFilter: true,
    fieldType: FieldType.DATE,
    filterType: FilterType.DATE_RANGE,
    cell: GetFieldRenderer(FieldType.DATE),
    filterFn: 'dateRange',
    size: 150,
  },
  {
    header: t('orders.recurring.ends_after'),
    enableSorting: true,
    accessorKey: 'end_after',
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.NUMBER,
    filterType: FilterType.NUMBER_RANGE,
    cell: GetFieldRenderer(FieldType.NUMBER),
    filterFn: 'inNumberRange',
    size: 100,
  },
  {
    header: t('orders.titles.project_no'),
    accessorKey: 'project_no',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 100,
  },
  {
    header: t('orders.titles.current_balance'),
    accessorKey: 'balance',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.MONEY,
    filterType: FilterType.NUMBER_RANGE,
    cell: GetFieldRenderer(FieldType.MONEY),
    filterFn: 'inNumberRange',
    sortingFn: 'money',
    size: 100,
  },
  {
    header: t('orders.titles.amount_gross'),
    accessorKey: 'amount',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.MONEY,
    filterType: FilterType.NUMBER_RANGE,
    cell: GetFieldRenderer(FieldType.MONEY),
    filterFn: 'inNumberRange',
    sortingFn: 'money',
    size: 100,
  },
  {
    header: t('orders.titles.created_on'),
    accessorKey: 'created_on',
    enableSorting: true,
    enableColumnFilter: true,
    fieldType: FieldType.DATE,
    filterType: FilterType.DATE_RANGE,
    cell: GetFieldRenderer(FieldType.DATE),
    filterFn: 'dateRange',
    size: 150,
  },
  {
    header: t('orders.titles.approved_by'),
    accessorKey: 'approved_by_name',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 150,
  },
  {
    header: t('orders.titles.rejected_by'),
    accessorKey: 'rejected_by_name',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 150,
  },
  {
    header: t('orders.titles.cleared_by'),
    accessorKey: 'cleared_by_names',
    enableSorting: false,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: (info: any) => {
      const names: string[] = info.getValue() ?? [];
      if (!names.length) return '-';
      const joined = names.join(', ');
      return h('span', { title: joined, class: 'tw-truncate tw-block tw-max-w-full' }, joined);
    },
    size: 200,
  },
  {
    header: t('orders.titles.status'),
    accessorKey: 'status_display',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.BADGE,
    filterType: FilterType.MULTI_SELECT,
    filterFn: 'enumFilter',
    cell: GetStatusRenderer(orderStatusOptions),
    filterOptions: orderStatusOptions,
    size: 150,
  },
  {
    header: t('orders.titles.clarification_requested'),
    accessorKey: 'is_clarification_requested',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.BOOLEAN,
    filterType: FilterType.BOOLEAN,
    filterFn: 'boolean',
    cell: GetFieldRenderer(FieldType.BOOLEAN),
    size: 150,
  },
  {
    header: t('requests.titles.range_in_months'),
    accessorKey: 'range_in_months',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.NUMBER,
    cell: GetFieldRenderer(FieldType.NUMBER),
    filterType: FilterType.NUMBER_RANGE,
    filterFn: 'inNumberRange',
    size: 100,
  },
  {
    header: t('orders.titles.expected_processing_date'),
    accessorKey: 'expected_date_processing',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.DATE,
    filterType: FilterType.DATE_RANGE,
    cell: GetFieldRenderer(FieldType.DATE),
    filterFn: 'dateRange',
    size: 150,
  },
  {
    header: t('orders.titles.order_triggered'),
    accessorKey: 'order_triggered',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.BOOLEAN,
    filterType: FilterType.BOOLEAN,
    filterFn: 'boolean',
    cell: GetFieldRenderer(FieldType.BOOLEAN),
    size: 100,
  },
  {
    header: t('orders.titles.triggered_on'),
    accessorKey: 'order_triggered_on',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.DATE,
    filterType: FilterType.DATE_RANGE,
    cell: GetFieldRenderer(FieldType.DATE),
    filterFn: 'dateRange',
    size: 150,
  },
  {
    header: t('orders.titles.prem_insv_proc'),
    accessorKey: 'preliminary_insolvency_proceedings',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.BOOLEAN,
    filterType: FilterType.BOOLEAN,
    filterFn: 'boolean',
    cell: GetFieldRenderer(FieldType.BOOLEAN),
    size: 100,
  },
  {
    header: t('orders.titles.supplier_order_no'),
    accessorKey: 'order_no_supplier',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 100,
  },
  {
    header: t('common.headers.actions'),
    accessorKey: 'actions',
    enableSorting: false,
    enableColumnFilter: false,
    enableHiding: false,
    fieldType: FieldType.TEXT,
    cell: info =>
      h(RecurringOrdersTableRowActions, {
        key: `${info.row.original.id}-${info.row.original.status_display}-${info.row.original.is_clarification_requested}`,
        order: info.row.original,
        onReloadTable: fetchRecurringOrders,
      }),
    size: 50,
  },
];

const handleRowSelectionChange = (selection: any[]) => {
  // Update cache: add newly selected rows from current page
  selection.forEach(row => {
    selectedRowsCache.value.set(String(row.id), row);
  });

  // Remove deselected rows: find rows in current page that are no longer selected
  const currentPageIds = new Set(orderTableData.value.map(row => String(row.id)));
  const selectedIds = new Set(selection.map(row => String(row.id)));

  currentPageIds.forEach(id => {
    if (!selectedIds.has(id)) {
      selectedRowsCache.value.delete(id);
    }
  });

  // Update selectedOrders with all cached rows
  selectedOrders.value = Array.from(selectedRowsCache.value.values());
};
</script>
